import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';

import { Dialog, DialogContent } from '../../ui/dialog';
import { Drawer, DrawerContent } from '../../ui/drawer';

type ModalProps = {
	show: boolean;
	onClose: () => void;
	headerAction?: React.ReactNode;
	children: React.ReactNode;
	classes?: {
		content?: string;
	};
};

const useIsMobile = (breakpoint = 768) => {
	const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < breakpoint);

	useEffect(() => {
		const mq = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
		const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
		mq.addEventListener('change', handler);
		return () => mq.removeEventListener('change', handler);
	}, [breakpoint]);

	return isMobile;
};

const ModalHeader = ({ headerAction, onClose }: { headerAction?: React.ReactNode; onClose: () => void }) => (
	<div className="flex items-center justify-between px-5 py-4 flex-shrink-0">
		<div>{headerAction}</div>
		<button onClick={onClose} className="p-1 hover:bg-gray-100 rounded transition-colors" aria-label="Close">
			<X size={20} className="text-gray-900" />
		</button>
	</div>
);

const ModalV1 = ({ show, onClose, headerAction, children, classes }: ModalProps) => {
	const isMobile = useIsMobile();

	if (isMobile) {
		return (
			<Drawer open={show} onOpenChange={(open) => !open && onClose()}>
				<DrawerContent
					className={`p-0 flex flex-col rounded-t-3xl bg-white after:hidden focus:outline-none ${classes?.content ? classes?.content : 'max-h-[85vh]'}`}
				>
					<div className="flex justify-center pt-1 pb-1 flex-shrink-0">
						<div className="w-10 h-1 rounded-full bg-gray-200" />
					</div>

					<ModalHeader headerAction={headerAction} onClose={onClose} />

					<div className="overflow-y-auto flex-1">{children}</div>
				</DrawerContent>
			</Drawer>
		);
	}

	return (
		<Dialog open={show} onOpenChange={(open) => !open && onClose()}>
			<DialogContent
				showCloseButton={false}
				className="p-0 gap-0 max-w-md max-h-[80vh] flex flex-col overflow-hidden rounded-2xl bg-white"
			>
				<ModalHeader headerAction={headerAction} onClose={onClose} />
				<div className="overflow-y-auto flex-1">{children}</div>
			</DialogContent>
		</Dialog>
	);
};

export default ModalV1;
